import {
  AcceptMessage,
  OfferMessage,
  SignMessage,
} from '@atomicfinance/bitcoin-dlc-provider';
import axios from 'axios';
import crypto from 'crypto';
import { PrivateKey, PublicKey, utils } from 'eciesjs';
import secp2561k1 from 'secp256k1';
import config from '../config/config';
import { ELECTION_RESOLUTION_DATE } from '../containers/NewBet';
import { client as batchClient } from '../services/batchChainClient';
import { sha256, urlEncode } from '../utils/Utils';
import { Bitcoin } from './chainClient';
import { getNumContracts } from '../utils/LocalStorage';

let cached: TowerService;
const http = axios.create({
  validateStatus: function (status) {
    return status >= 200 && status < 500;
  },
});

export default class TowerService {
  private client: Bitcoin;
  private _privateKey: PrivateKey;
  private initialized = false;

  constructor(private towerUri: string, client?: Bitcoin) {
    if (client) this.client = client;
  }

  async initialize() {
    if (this.initialized) return this;
    console.log('initialized');
    const staticKeyPair = await this.getFundingKeyPair(0);
    console.log(staticKeyPair);
    this._privateKey = new PrivateKey(staticKeyPair.privateKey);
    this.initialized = true;
    return this;
  }

  setClient(client: Bitcoin) {
    this.client = client;
  }

  async getNumContracts(pubkey: string) {
    const numContracts = await getNumContracts()

    const acceptances = (await this.getRemoteMessages(pubkey)) || []
    const offersList: OfferMessage[] = []
    const offersResponse = await this.getOffers(pubkey)

    if (offersResponse) {
      const { data: _offers } = offersResponse;

      for (let i = 0; i < _offers.length; i++) {
        const offer = OfferMessage.fromJSON(JSON.parse(_offers[i].offerData));
        offersList.push(offer)
      }
    }

    return Math.max(numContracts, offersList.length + acceptances.length)
  }

  async getContracts() {
    const contractData = (await http.get(`${this.towerUri}/contract/`)).data;

    return contractData;
  }

  async findTwitterId(contractUUID: string) {
    const contractData = (
      await http.get(`${this.towerUri}/contract/${contractUUID}`)
    ).data;

    const offerorPubkey = contractData.offererPubkey;

    const userData = (
      await http.get(`${this.towerUri}/users/pubkey/${offerorPubkey}`)
    ).data;

    return userData.twitterId;
  }

  async getOffersByTwitter(id: string) {
    const _offers = (
      await http.get(`${this.towerUri}/users/twitter_id/${id}/offers`)
    ).data;

    console.log(_offers);

    const offers = await Promise.all(
      _offers.map(async (_offer) => {
        const offer = OfferMessage.fromJSON(JSON.parse(_offer.offerData));
        const { localPartyInputs: { utxos } } = offer

        const betSize =
          offer.localCollateral.GetSatoshiAmount() +
          offer.remoteCollateral.GetSatoshiAmount();
        const betAmount = offer.localCollateral.GetSatoshiAmount();
        const betAmountCounter = offer.remoteCollateral.GetSatoshiAmount();
        const prediction = offer.outcomes[0].message;

        const offerAddresses = utxos.map(
          (utxo) => utxo.address
        );

        const _utxos = await batchClient.client.getMethod(
          'getUnspentTransactions'
        )(offerAddresses);
        const acceptable = _offer.contractStage === 'OFFER' && _utxos.length > 0;

        return {
          contractId: offer.contractId,
          contractStage: _offer.contractStage,
          betDetails: {
            event: 'election',
            resolutionDate: ELECTION_RESOLUTION_DATE,
            prediction,
            betSize,
            betAmount,
            betAmountCounter,
          },
          offer,
          acceptable,
        };
      })
    );

    return offers;
  }

  async getOffer(contractUUID: string) {
    const payload = (
      await http.get(`${this.towerUri}/contract/${contractUUID}/offer`)
    ).data;

    const contractData = (
      await http.get(`${this.towerUri}/contract/${contractUUID}`)
    ).data;

    const offerorPubkey = contractData.offererPubkey;
    const contractStage = contractData.contractStage;
    const offerString = payload.offerData;
    const offerSignature = payload.offerSignature;
    const offer = JSON.parse(offerString);

    if (
      !secp2561k1.ecdsaVerify(
        Buffer.from(offerSignature, 'base64'),
        sha256(offerString),
        PublicKey.fromHex(offerorPubkey).compressed
      )
    ) {
      throw new Error(
        `Signature verification failed: contract ${contractUUID}`
      );
    }

    const offerMessage = OfferMessage.fromJSON(offer);
    offerMessage.contractStage = contractStage;
    return offerMessage;
  }

  postTwitterVerification(twitterId: string) {
    if (!this.client) throw new Error('Private key not set');

    const twitterIdHash = sha256(twitterId);
    const signature = urlEncode(
      Buffer.from(
        secp2561k1.ecdsaSign(twitterIdHash, this._privateKey.secret).signature
      ).toString('base64')
    );

    return http.post(
      `${this.towerUri}/users/pubkey/${this.publicKey}/verify_twitter`,
      {
        twitterId,
        signature,
      }
    );
  }

  postOffer(offer: OfferMessage) {
    if (!this.client) throw new Error('Private key not set');

    const offerString = JSON.stringify(offer);
    const offerHash = sha256(offerString);
    const signature = urlEncode(
      Buffer.from(
        secp2561k1.ecdsaSign(offerHash, this._privateKey.secret).signature
      ).toString('base64')
    );

    return http.post(`${this.towerUri}/users/pubkey/${this.publicKey}/offers`, {
      offer: offerString,
      signature,
    });
  }

  async getAccept(contractUUID: string, startingIndex: number) {
    const { timestamp, timestampSignature } = await this.generateToken();

    const payload = (
      await http.get(`${this.towerUri}/contract/${contractUUID}/accept`, {
        headers: {
          'X-Request-Timestamp': timestamp,
          'X-Signed-Token': timestampSignature,
        },
      })
    ).data;

    const keyPair = await this.getFundingKeyPair(startingIndex);
    const ephemeralPrivateKey = new PrivateKey(keyPair.privateKey);

    if (payload.length === 0 || payload.errorCode) return null;
    const { encryptedAcceptanceData, offereePubkey } = payload[0];

    const derivedSharedSecret = PublicKey.fromHex(offereePubkey).decapsulate(
      ephemeralPrivateKey
    );

    const decryptedMessage = utils
      .aesDecrypt(
        derivedSharedSecret,
        Buffer.from(encryptedAcceptanceData, 'hex')
      )
      .toString();

    console.log(JSON.parse(decryptedMessage));
    console.log(decryptedMessage);
    const acceptMessage = AcceptMessage.fromJSON(JSON.parse(decryptedMessage));
    return acceptMessage;
  }

  async postAccept(accept: AcceptMessage, to: string, startingIndex: number) {
    if (!this._privateKey) throw new Error('Private key not set');

    const acceptString = JSON.stringify(accept);

    console.log('acceptString', acceptString);

    const keyPair = await this.getFundingKeyPair(startingIndex);
    const ephemeralPrivateKey = new PrivateKey(keyPair.privateKey);
    const ephemeralPubkey = ephemeralPrivateKey.publicKey.compressed.toString(
      'hex'
    );

    const publicKeyHashHash = sha256(ephemeralPubkey);
    const signature = urlEncode(
      Buffer.from(
        secp2561k1.ecdsaSign(publicKeyHashHash, this._privateKey.secret)
          .signature
      ).toString('base64')
    );

    const derivedSharedSecret = ephemeralPrivateKey.encapsulate(
      PublicKey.fromHex(to)
    );

    const encryptedAcceptance = utils
      .aesEncrypt(derivedSharedSecret, Buffer.from(acceptString, 'utf8'))
      .toString('hex');

    const { timestamp, timestampSignature } = await this.generateToken();

    return await http.post(
      `${this.towerUri}/contract/${accept.contractId}/accept`,
      {
        encryptedAcceptance,
        pubkey: ephemeralPubkey,
        pubkeySignature: signature,
        staticPubkey: this.publicKey,
      },
      {
        headers: {
          'X-Request-Timestamp': timestamp,
          'X-Signed-Token': timestampSignature,
        },
      }
    );
  }

  async getSign(
    contractUUID: string,
    offererPubkey: string,
    startingIndex: number
  ) {
    const { timestamp, timestampSignature } = await this.generateToken(
      startingIndex
    );

    console.log(startingIndex);

    const payload = (
      await http.get(`${this.towerUri}/contract/${contractUUID}/sign`, {
        headers: {
          'X-Request-Timestamp': timestamp,
          'X-Signed-Token': timestampSignature,
        },
      })
    ).data;

    if (!payload || payload.errorCode) return;

    const { encryptedSignData } = payload;

    const keyPair = await this.getFundingKeyPair(startingIndex);
    const ephemeralPrivateKey = new PrivateKey(keyPair.privateKey);

    console.log(ephemeralPrivateKey);

    const derivedSharedSecret = PublicKey.fromHex(offererPubkey).decapsulate(
      ephemeralPrivateKey
    );

    const decryptedMessage = utils
      .aesDecrypt(derivedSharedSecret, Buffer.from(encryptedSignData, 'hex'))
      .toString();

    console.log(JSON.parse(decryptedMessage));
    console.log(decryptedMessage);
    const signMessage = JSON.parse(decryptedMessage);
    return signMessage;
  }

  async getRemoteSign(
    contractUUID: string,
    remotePubkey: string,
    startingIndex: number
  ) {
    const { timestamp, timestampSignature } = await this.generateToken(
      startingIndex
    );

    console.log(startingIndex);

    const payload = (
      await http.get(`${this.towerUri}/contract/${contractUUID}/sign`, {
        headers: {
          'X-Request-Timestamp': timestamp,
          'X-Signed-Token': timestampSignature,
        },
      })
    ).data;

    console.log('payload', payload);

    if (!payload || payload.errorCode) return;

    const { encryptedSignData } = payload;

    console.log('startingIndex', startingIndex);

    const keyPair = await this.getFundingKeyPair(startingIndex);
    const ephemeralPrivateKey = new PrivateKey(keyPair.privateKey);

    const derivedSharedSecret = ephemeralPrivateKey.encapsulate(
      PublicKey.fromHex(remotePubkey)
    );

    const decryptedMessage = utils
      .aesDecrypt(derivedSharedSecret, Buffer.from(encryptedSignData, 'hex'))
      .toString();

    console.log(JSON.parse(decryptedMessage));
    console.log(decryptedMessage);
    const signMessage = JSON.parse(decryptedMessage);
    return signMessage;
  }

  async postSign(sign: SignMessage, to: string, startingIndex: number) {
    if (!this._privateKey) throw new Error('Private key not set');

    const signString = JSON.stringify(sign);

    console.log('signString', signString);

    const keyPair = await this.getFundingKeyPair(startingIndex);
    const ephemeralPrivateKey = new PrivateKey(keyPair.privateKey);

    const derivedSharedSecret = ephemeralPrivateKey.encapsulate(
      PublicKey.fromHex(to)
    );

    const encryptedSign = utils
      .aesEncrypt(derivedSharedSecret, Buffer.from(signString, 'utf8'))
      .toString('hex');

    const { timestamp, timestampSignature } = await this.generateToken();

    return await http.post(
      `${this.towerUri}/contract/${sign.contractId}/sign`,
      {
        encryptedSign,
        offereePubkey: to,
      },
      {
        headers: {
          'X-Request-Timestamp': timestamp,
          'X-Signed-Token': timestampSignature,
        },
      }
    );
  }

  getOffers(pubkey: string) {
    return axios.get(`${this.towerUri}/users/pubkey/${pubkey}/offers`);
  }

  getUserInfo(pubkey: string) {
    return axios.get(`${this.towerUri}/users/pubkey/${pubkey}`);
  }

  async getAllActiveUsers() {
    return (await axios.get(`${this.towerUri}/users/active`)).data;
  }

  async getRemoteMessagesWithoutParse(pubkey: string) {
    const payload = (
      await axios.get(`${this.towerUri}/users/pubkey/${pubkey}/acceptances`)
    ).data;

    return payload;
  }

  async getRemoteMessages(pubkey: string) {
    const payload = (
      await axios.get(`${this.towerUri}/users/pubkey/${pubkey}/acceptances`)
    ).data;

    if (payload.length === 0 || payload.errorCode) return null;

    const acceptances = payload;
    const parsedAcceptances: any[] = [];
    for (let i = 0; i < acceptances.length; i++) {
      const acceptance = acceptances[i];

      const offerMessage = await this.getOffer(acceptance.contractUUID);
      const { acceptMessage, startingIndex } = await this.parseAcceptPayload(
        acceptance,
        offerMessage.localPartyInputs.fundPublicKey
      );
      const signMessage = await this.getSign(
        acceptance.contractUUID,
        offerMessage.localPartyInputs.fundPublicKey,
        startingIndex
      );

      parsedAcceptances.push({
        offerMessage,
        acceptMessage,
        signMessage,
        startingIndex,
      });
    }

    return parsedAcceptances;
  }

  async parseAcceptPayload(payload, localPubkey): Promise<any> {
    const { encryptedAcceptanceData, offereePubkey } = payload;

    const startingIndex =
      (await this.getStartingIndexByPubkey(offereePubkey)) - 1;

    const keyPair = await this.getFundingKeyPair(startingIndex);
    const ephemeralPrivateKey = new PrivateKey(keyPair.privateKey);

    const derivedSharedSecret = ephemeralPrivateKey.encapsulate(
      PublicKey.fromHex(localPubkey)
    );

    const decryptedMessage = utils
      .aesDecrypt(
        derivedSharedSecret,
        Buffer.from(encryptedAcceptanceData, 'hex')
      )
      .toString();

    console.log(JSON.parse(decryptedMessage));
    console.log(decryptedMessage);
    const acceptMessage = AcceptMessage.fromJSON(JSON.parse(decryptedMessage));
    return { acceptMessage, startingIndex };
  }

  hasPrivateKey() {
    return !!this._privateKey;
  }

  get publicKey() {
    return this._privateKey.publicKey.compressed.toString('hex');
  }

  set privateKey(privateKey: Buffer) {
    this._privateKey = new PrivateKey(privateKey);
  }

  async generateToken(startingIndex = 0) {
    let privateKey = this._privateKey;
    if (startingIndex !== 0) {
      privateKey = new PrivateKey(
        (await this.getFundingKeyPair(startingIndex)).privateKey
      );
    }

    console.log(startingIndex, privateKey.publicKey.compressed.toString('hex'));

    const timestamp = Math.round(new Date().getTime() / 1000).toString();
    const timestampHash = crypto
      .createHash('sha256')
      .update(timestamp)
      .digest();

    const timestampSignature = urlEncode(
      Buffer.from(
        secp2561k1.ecdsaSign(timestampHash, privateKey.secret).signature
      ).toString('base64')
    );

    return { timestamp, timestampSignature };
  }

  private async getFundingPubkey(startingIndex) {
    const addresses = await this.client.getMethod('getAddresses')(
      startingIndex,
      2,
      false
    );

    return addresses[1];
  }

  private async getFundingKeyPair(startingIndex) {
    const addresses = await this.client.getMethod('getAddresses')(
      startingIndex,
      2,
      false
    );

    const fundPrivateKeyPair = await this.client.getMethod('keyPair')(
      addresses[1].derivationPath
    );

    return fundPrivateKeyPair;
  }

  private async getStartingIndexByPubkey(pubkey: string) {
    const address = await this.client.getMethod('getAddressFromPublicKey')(
      typeof pubkey === 'string' ? Buffer.from(pubkey, 'hex') : pubkey
    );
    const walletAddress = await this.client.getMethod('getWalletAddress')(
      address
    );

    return walletAddress.index;
  }
}

export const hasTowerCredentials = () => {
  return cached && cached.hasPrivateKey();
};

export const getTowerService = (client?: Bitcoin) => {
  if (cached) {
    if (!cached.hasPrivateKey() && client) cached.setClient(client);
    return cached;
  }
  cached = new TowerService(config.tower.url, client);
  return cached;
};
